import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  ArrowUpRight,
  BellRing,
  CalendarClock,
  CheckCircle2,
  ExternalLink,
  History,
  Info,
  RefreshCw,
} from "lucide-react";
import { actionEventWorkspace } from "@/lib/api";
import {
  businessActivityEvents,
  deriveActionItems,
  deriveBudgetPositions,
  sourceHref,
} from "@/lib/product-intelligence.mjs";
import { Button } from "@/components/ui/button";
import { displayDate, money, titleCase } from "@/lib/utils";

type Permissions = {
  requisition?: boolean;
  approve: boolean;
  procure: boolean;
  receive: boolean;
  finance: boolean;
  manage: boolean;
};
type Props = {
  organisationId: string;
  currentUserId: string;
  refresh: number;
  permissions: Permissions;
  onNavigate: (view: string, recordId?: string) => void;
};

export function ActionEventCenter({
  organisationId,
  currentUserId,
  refresh,
  permissions,
  onNavigate,
}: Props) {
  const [data, setData] =
    useState<Awaited<ReturnType<typeof actionEventWorkspace>>>();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError("");
    actionEventWorkspace(organisationId)
      .then((value) => {
        if (live) setData(value);
      })
      .catch((reason: Error) => {
        if (live) setError(reason.message);
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [organisationId, refresh]);

  const actions = useMemo(
    () => deriveActionItems(data || {}, permissions, currentUserId),
    [currentUserId, data, permissions],
  );
  const events = useMemo(
    () => businessActivityEvents(data?.events || []),
    [data],
  );
  const budgetPositions = useMemo(
    () => deriveBudgetPositions(data?.budgets || []),
    [data],
  );
  const showBudgetPosition =
    permissions.manage || permissions.approve || permissions.finance;

  if (loading)
    return (
      <State title="Loading actions and events">
        Reading current workflow states and immutable activity.
      </State>
    );
  if (error || !data)
    return (
      <State title="Unable to load the Action & Event Centre">
        {error || "No tenant-scoped data was returned."}
      </State>
    );

  return (
    <div className="page-stack">
      <header className="page-heading">
        <div>
          <p className="eyebrow">WORK QUEUE & IMMUTABLE HISTORY</p>
          <h1>Action & Event Centre</h1>
          <p>
            Pending work is derived from current document state and your
            permissions. Activity comes from RAQOZ’s immutable event history.
          </p>
        </div>
        <div
          className="action-summary"
          aria-label={`${actions.length} actions require attention`}
        >
          <CheckCircle2 aria-hidden="true" className="size-5" />
          <strong>{actions.length}</strong>
          <span>requires action</span>
        </div>
      </header>

      <section
        className="boundary-panel"
        aria-labelledby="delivery-boundary-title"
      >
        <Info aria-hidden="true" className="size-5" />
        <div>
          <h2 id="delivery-boundary-title">Integration boundary</h2>
          <p>
            This centre does not imply email or webhook delivery. Templates,
            recipients, retries, delivery receipts and notification preferences
            are active only when a governed connector is configured and a
            delivery record exists. Opening this page never sends anything.
          </p>
        </div>
      </section>

      {showBudgetPosition && (
        <section className="surface p-5" aria-labelledby="budget-position-title">
          <div>
            <p className="eyebrow">FINANCIAL CONTEXT</p>
            <h2 id="budget-position-title" className="mt-1 font-serif text-xl">
              Overall budget consumed
            </h2>
            <p className="mt-1 text-sm text-[var(--text-secondary)]">
              Active reservations, approvals and commitments against active budget allocations.
            </p>
          </div>
          {budgetPositions.length ? (
            <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {budgetPositions.map((position) => (
                <div className="rounded-xl border border-[var(--border)] p-4" key={position.currency}>
                  <strong className="font-serif text-2xl font-normal">
                    {money(position.consumed, position.currency)}
                  </strong>
                  <span className="mt-1 block text-xs text-[var(--text-secondary)]">
                    of {money(position.allocated, position.currency)} · {position.consumedPercent.toFixed(1)}% consumed
                  </span>
                  <span className="mt-2 block text-xs font-semibold text-[var(--teal)]">
                    {money(position.remaining, position.currency)} remaining across {position.budgetCount} active budget{position.budgetCount === 1 ? "" : "s"}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-4 text-sm text-[var(--text-secondary)]">
              No active budget allocation is recorded for this organisation.
            </p>
          )}
        </section>
      )}

      <div className="grid gap-5 xl:grid-cols-[.92fr_1.08fr]">
        <Section
          icon={<BellRing className="size-4" />}
          title="Requires action"
          note="Action count—not an unread count."
        >
          {actions.length ? (
            <div className="divide-y divide-[var(--border)]">
              {actions.map((item) => (
                <a
                  className="action-row"
                  key={item.id}
                  href={item.href}
                  onClick={(event) => {
                    event.preventDefault();
                    onNavigate(
                      new URL(
                        item.href,
                        window.location.origin,
                      ).searchParams.get("view") || "audit",
                      item.entityId,
                    );
                  }}
                >
                  <span className="action-row__icon">
                    <CalendarClock className="size-4" aria-hidden="true" />
                  </span>
                  <span className="min-w-0">
                    <strong>{item.title}</strong>
                    <span>
                      {item.reference} · {item.detail}
                    </span>
                    {item.budget && (
                      <span>
                        {item.budget.category} budget: {money(item.budget.consumed, item.budget.currency)} of {money(item.budget.allocated, item.budget.currency)} consumed ({item.budget.consumedPercent.toFixed(1)}%)
                      </span>
                    )}
                  </span>
                  <span className="action-row__meta">
                    {item.dueAt ? displayDate(item.dueAt) : "Open"}
                    <ArrowUpRight className="size-4" aria-hidden="true" />
                  </span>
                </a>
              ))}
            </div>
          ) : (
            <Empty
              title="No actions require your role"
              detail="RAQOZ found no pending records that your current permissions can advance."
            />
          )}
        </Section>

        <Section
          icon={<History className="size-4" />}
          title="Recent activity"
          note="Newest event first; ties use the immutable event identifier."
        >
          {events.length ? (
            <div className="max-h-[660px] divide-y divide-[var(--border)] overflow-auto">
              {events.map((event: any) => {
                const href = sourceHref(event.entity_type, event.entity_id);
                const view =
                  new URL(href, window.location.origin).searchParams.get(
                    "view",
                  ) || "audit";
                return (
                  <div className="event-row" key={event.id}>
                    <span className="event-dot" aria-hidden="true" />
                    <span className="min-w-0">
                      <strong>{titleCase(String(event.display_action))}</strong>
                      <span>
                        {titleCase(event.entity_type)} ·{" "}
                        {event.profiles?.full_name || "System"}
                      </span>
                    </span>
                    <span className="event-row__meta">
                      <time dateTime={event.created_at}>
                        {displayDate(event.created_at, true)}
                      </time>
                      <Button
                        size="sm"
                        variant="ghost"
                        asChild
                        aria-label={`Open source record for ${titleCase(event.action)}`}
                      >
                        <a
                          href={href}
                          onClick={(click) => {
                            click.preventDefault();
                            onNavigate(view, event.entity_id);
                          }}
                        >
                          <ExternalLink className="size-4" aria-hidden="true" />
                        </a>
                      </Button>
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <Empty
              title="No recorded activity"
              detail="Events appear here when governed workflows write immutable audit records."
            />
          )}
        </Section>
      </div>
    </div>
  );
}

export function ActionCountButton({
  organisationId,
  currentUserId,
  refresh,
  permissions,
  onOpen,
}: Omit<Props, "onNavigate"> & { onOpen: () => void }) {
  const [count, setCount] = useState<number | null>(null);
  useEffect(() => {
    let live = true;
    actionEventWorkspace(organisationId)
      .then((data) => {
        if (live)
          setCount(
            deriveActionItems(data, permissions, currentUserId).length,
          );
      })
      .catch(() => {
        if (live) setCount(null);
      });
    return () => {
      live = false;
    };
  }, [currentUserId, organisationId, refresh, permissions]);
  return (
    <Button
      variant="outline"
      size="sm"
      className="relative"
      onClick={onOpen}
      aria-label={
        count == null
          ? "Open Action and Event Centre"
          : `Open Action and Event Centre; ${count} actions require attention`
      }
    >
      <BellRing className="size-4" aria-hidden="true" />
      <span className="hidden md:inline">Actions</span>
      {count != null && (
        <span className="action-count" aria-hidden="true">
          {count}
        </span>
      )}
    </Button>
  );
}

function Section({
  icon,
  title,
  note,
  children,
}: {
  icon: ReactNode;
  title: string;
  note: string;
  children: ReactNode;
}) {
  return (
    <section className="surface overflow-hidden">
      <header className="section-heading">
        <span className="section-heading__icon" aria-hidden="true">
          {icon}
        </span>
        <div>
          <h2>{title}</h2>
          <p>{note}</p>
        </div>
      </header>
      {children}
    </section>
  );
}

function Empty({ title, detail }: { title: string; detail: string }) {
  return (
    <div className="empty-state">
      <RefreshCw className="size-5" aria-hidden="true" />
      <h3>{title}</h3>
      <p>{detail}</p>
    </div>
  );
}

function State({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="surface p-8" role="status">
      <h2 className="text-xl font-semibold">{title}</h2>
      <p className="mt-2 text-sm text-[var(--text-secondary)]">{children}</p>
    </div>
  );
}
